import clsx from "clsx";
import { twMerge } from "tailwind-merge";

import { useAdoptedBudgetMix } from "@features/mmm/hooks/use-adopted-budget-mix";
import { ComboboxButton } from "@headlessui/react";
import { Select } from "@shared/selects";
import { renderChevronAnimation } from "@shared/selects/utils";

import { type Kpi } from "../explore-budgets/client-setting/types";

export type Props = {
  options: { label: string; value: string }[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  inheritedClientSettingKpi?: Kpi | null | undefined;
};

export const KpiSelect = ({
  options,
  value,
  onChange,
  className,
  inheritedClientSettingKpi,
}: Props) => {
  const { adoptedKpiName } = useAdoptedBudgetMix();

  return (
    <Select
      className={twMerge("w-[480px]", className)}
      value={value}
      options={options}
      onChange={onChange}
      noPortal
      buttonRenderer={({ open, value, displayValue }) => (
        <ComboboxButton
          as="div"
          className="nb-input flex justify-between items-center cursor-pointer bg-v2-primary text-v2-primary border-v2-secondary"
        >
          <div className="text-ellipsis whitespace-nowrap overflow-hidden max-w-full flex-grow text-left">
            {displayValue}
          </div>
          {adoptedKpiName === value && <AdoptedChip />}
          {adoptedKpiName !== value &&
            inheritedClientSettingKpi?.kpi_name === value && <InheritedChip />}
          <span
            className={clsx({ "text-cosmic+1": open }, "pl-2 justify-self-end")}
          >
            {renderChevronAnimation(open, false)}
          </span>
        </ComboboxButton>
      )}
      menuItemRender={({ option, value, onChange, active, selected }) => (
        <div
          className={clsx(
            (active || selected) && !option.disabled
              ? "bg-cosmic-3 font-semibold"
              : "hover:bg-cosmic-4 font-normal",
            option.disabled && "bg-gray-3 pointer-events-none",
            "px-4 py-2 cursor-pointer text-v2-primary flex items-center justify-between whitespace-nowrap",
          )}
        >
          <div>{option.label}</div>
          {adoptedKpiName === option.value && <AdoptedChip className="mr-5" />}
          {adoptedKpiName !== option.value &&
            inheritedClientSettingKpi?.kpi_name === option.value && (
              <InheritedChip />
            )}
        </div>
      )}
    />
  );
};

export const AdoptedChip = ({ className }: { className?: string }) => (
  <div
    className={clsx(
      "rounded-full bg-pluto text-white leading-0 text-xxs py-2.5 px-3 ml-3",
      className,
    )}
  >
    ADOPTED
  </div>
);
export const InheritedChip = ({ className }: { className?: string }) => (
  <div className="rounded-full bg-cosmic text-white leading-0 text-xxs py-2.5 px-3 ml-3">
    INHERITED
  </div>
);
